import { action } from '@ember/object';
import Component from '@glimmer/component';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

interface ScrollableTextComponentInterface {
	Blocks: { default: [] }; // this is needed for yield
}

export default class ScrollableTextComponent extends Component<ScrollableTextComponentInterface> {
	node: HTMLElement | null = null;

	tolerance = 1;

	private focused = false;

	// scrolling is not tracked and updated in template bcos:
	// 1. setter bcos we need the calculations in the logic immediately (class would be set in next runloop)
	// 2. perf reasons, minimizing template changes
	private _scrolling = false;

	get scrolling() {
		return this._scrolling;
	}

	set scrolling(value: boolean) {
		this._scrolling = value;
		this.node?.classList.toggle('scrolling', value);
	}

	@action
	handleMouseEnter() {
		this.focused = true;
		setTimeout(() => {
			this.scrollFn();
		});
	}

	@action
	handleMouseLeave() {
		this.focused = false;
		const node = this.node;
		if (node) {
			$(node).find('span').data('scrollDirection', null).css({
				left: 0,
			});
		}
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node;

		node.addEventListener('mouseenter', this.handleMouseEnter);
		node.addEventListener('mouseleave', this.handleMouseLeave);
	}

	@action
	teardownView() {
		const node = this.node;
		if (!node) {
			return;
		}
		node.removeEventListener('mouseenter', this.handleMouseEnter);
		node.removeEventListener('mouseleave', this.handleMouseLeave);
		this.node = null;
	}

	private scrollFn() {
		const node = this.node;
		if (!node) {
			return;
		}

		if (!this.focused) {
			this.scrolling = false;
			return;
		}

		const parent = $(node),
			text = parent.find('span.scrollable'),
			ctWidth = parent.width() || 0,
			textWidth = text.outerWidth() || 0;

		if (textWidth <= ctWidth) {
			this.scrolling = false;
			return;
		}

		const textAndCtWidthDiff = Math.abs(textWidth - ctWidth);

		if (textAndCtWidthDiff <= this.tolerance) {
			this.scrolling = false;
			return;
		}

		if (!this.scrolling) {
			this.scrolling = true;
		}

		let direction = (text.data('scrollDirection') as string | undefined) || 'left',
			timeout = 20,
			leftPos = parseInt(text.css('left'), 10);

		// go left til end fits, then go back right, repeat
		if (leftPos + textWidth <= ctWidth && direction === 'left') {
			direction = 'right';
			timeout = 300;
		}
		if (leftPos === 0 && direction === 'right') {
			direction = 'left';
			timeout = 300;
		}

		leftPos = leftPos + (direction === 'right' ? 1 : -1);
		text.data('scrollDirection', direction);

		text.css({
			left: leftPos,
		});
		setTimeout(() => {
			this.scrollFn();
		}, timeout);
	}

	<template>
		<div class="scrollable-text" {{didInsert this.setupView}} {{willDestroy this.teardownView}}>
			<span class="scrollable">
				{{yield}}
			</span>
		</div>
	</template>
}
